import { RocketOutlined } from '@ant-design/icons'
import { Button, Flex, notification, theme } from 'antd'
import { useEffect, useState } from 'react'
import { useAppVersionCheck } from '../hooks/useAppVersionCheck'

const NOTIFICATION_KEY = 'new-version'

// One object for every render: antd rebuilds its API when the config changes.
// `bottom` keeps the card above the DevTools floating button (bottom right).
const NOTIFICATION_CONFIG = {
  placement: 'bottomRight',
  bottom: 88,
  stack: false,
} as const

/**
 * "New version available" card, bottom right, once a newer back-office is
 * deployed (same pattern as Kitchen's NewVersionToast). It never blocks the
 * page and never reloads by itself, so work in progress is not lost:
 * "Refresh" reloads, the close icon hides it until the next deployment.
 */
const UpdateBanner = () => {
  const { token } = theme.useToken()
  const { newerVersion, reload } = useAppVersionCheck()
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null)
  const [api, contextHolder] = notification.useNotification(NOTIFICATION_CONFIG)

  useEffect(() => {
    if (newerVersion === null || newerVersion === dismissedVersion) return

    api.open({
      key: NOTIFICATION_KEY,
      duration: 0,
      role: 'status',
      icon: <RocketOutlined style={{ color: token.colorPrimary }} />,
      title: 'New version available',
      description: (
        <Flex vertical align="start" gap={6}>
          <span>Refresh to get the latest back-office.</span>
          <Button
            type="link"
            onClick={reload}
            style={{ padding: 0, height: 'auto', fontWeight: 600 }}
          >
            Refresh
          </Button>
        </Flex>
      ),
      onClose: () => setDismissedVersion(newerVersion),
    })
  }, [api, newerVersion, dismissedVersion, reload, token.colorPrimary])

  return contextHolder
}

export default UpdateBanner
